import {
  type KilnHeatConsumptionsResponse,
  type QuarterlyForecastKilnHeatConsumptionsHierarchy,
  type MtmYtmRequestPayload,
  formatDateUtcToTimezoneDateOnly,
  PERFORMANCE_KPI_REQUEST_STALE_TIME
} from '@domain/kpis'
import {ApiClient} from '@hconnect/apiclient'
import {Iso8601} from '@hconnect/uikit'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'

export const getKilnHeatConsumptionMtmYtmUrlSuffix = (showYTM?: boolean) =>
  showYTM ? 'kiln-heat-consumptions-ytm' : 'kiln-heat-consumptions-mtm'

export const getKilnHeatConsumptionMtmYtmQueryKeys = (requestPayload: MtmYtmRequestPayload) => [
  getKilnHeatConsumptionMtmYtmUrlSuffix(requestPayload.showYTM),
  formatDateUtcToTimezoneDateOnly(requestPayload.timestamp, requestPayload.timezone),
  requestPayload.locationId
]

export const getKilnHeatConsumption = async (
  {timestamp, locationId, timezone, showYTM}: MtmYtmRequestPayload,
  signal?: AbortSignal
) => {
  const referenceDate = formatDateUtcToTimezoneDateOnly(timestamp, timezone)

  const url = `/kpi/dashboard/${getKilnHeatConsumptionMtmYtmUrlSuffix(showYTM)}`
  const response = await ApiClient.axiosInstance.get<KilnHeatConsumptionsResponse>(url, {
    params: {locationId, date: referenceDate},
    signal
  })

  return response.data
}

export const useKilnHeatConsumption = <T>(
  payload: MtmYtmRequestPayload,
  options?: Omit<
    UseQueryOptions<KilnHeatConsumptionsResponse, AxiosError, T>,
    'queryKey' | 'queryFn'
  >
) => {
  const QueryKeys = getKilnHeatConsumptionMtmYtmQueryKeys(payload)

  return useQuery<KilnHeatConsumptionsResponse, AxiosError, T>({
    queryKey: QueryKeys,
    queryFn: async ({signal}) => {
      return await getKilnHeatConsumption(payload, signal)
    },
    staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME,
    ...options
  })
}

export const useKilnHeatConsumptionQuarterly = (
  timestamp: Iso8601,
  timezone: string,
  locationId?: string,
  options?: Omit<
    UseQueryOptions<QuarterlyForecastKilnHeatConsumptionsHierarchy | null, AxiosError>,
    'queryKey' | 'queryFn'
  >
) => {
  const referenceDate = formatDateUtcToTimezoneDateOnly(timestamp, timezone)

  return useQuery<QuarterlyForecastKilnHeatConsumptionsHierarchy | null, AxiosError>({
    queryKey: ['kiln-heat-consumptions-quarterly', referenceDate, locationId],
    queryFn: async ({signal}) => {
      if (!locationId) return Promise.resolve(null)

      const response =
        await ApiClient.axiosInstance.get<QuarterlyForecastKilnHeatConsumptionsHierarchy>(
          '/kpi/dashboard/kiln-heat-consumptions-qf',
          {
            params: {locationId, timestamp: referenceDate},
            signal
          }
        )
      return response.data
    },
    staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME,
    ...options
  })
}
